import { Link } from "react-router-dom";
import { ArrowRight, Phone, Mail, Clock, MapPin } from "lucide-react";
import { BRAND } from "@/lib/site";
import FinalCTA from "@/components/site/FinalCTA";
import SEO, { breadcrumbsLd, localBusinessLd } from "@/components/site/SEO";

const HIGHLIGHTS = [
  { title: "Direct I-75 access", body: "Two minutes from the I-75 / Royal Palm Blvd interchange." },
  { title: "Sawgrass Expressway", body: "Six minutes to the Sawgrass — a clear shot to Boca, Coral Springs, and the airports." },
  { title: "Walk to lunch", body: "Sixteen restaurants, three coffee bars, and a bank within a 5-minute walk." },
  { title: "Hotels close by", body: "Four national hotel brands within a mile, ideal for visiting clients." },
];

const LocationPage = () => (
  <>
    <SEO
      title="Office Location · 2600 Glades Circle, Weston, FL 33327"
      description="Weston Commercial Center is located at 2600 Glades Circle, Suite 100, Weston, FL 33327 — two minutes from I-75, six from the Sawgrass Expressway. Easy access from Davie, Sunrise and Pembroke Pines."
      path="/location"
      jsonLd={[
        breadcrumbsLd([
          { name: "Home", path: "/" },
          { name: "Location", path: "/location" },
        ]),
        localBusinessLd(BRAND),
      ]}
    />
    <section className="bg-[var(--bg)] border-b border-[var(--border)]">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-20 md:py-28">
        <div className="eyebrow">Location</div>
        <h1 className="hero-display text-[52px] md:text-[96px] mt-5 leading-[1.0] max-w-4xl">
          The <em className="italic text-[var(--primary)]">centre</em> of Weston.
        </h1>
        <p className="mt-7 max-w-2xl text-[16px] leading-relaxed text-[var(--text-muted)]">
          A short drive from anywhere in west Broward — and a five-minute walk
          to lunch. {BRAND.address.street}, {BRAND.address.city}, {BRAND.address.state} {BRAND.address.zip}.
        </p>
      </div>
    </section>

    <section className="bg-white border-b border-[var(--border)]">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-12">
        <div className="hairline overflow-hidden aspect-[16/8] bg-[var(--border)]">
          <iframe
            title="Weston Commercial Center map"
            src={BRAND.googleMapsEmbed}
            width="100%"
            height="100%"
            style={{ border: 0, display: "block" }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </section>

    <section className="bg-[var(--bg)] border-b border-[var(--border)]">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="eyebrow">Why this address</div>
            <h2 className="section-title text-[36px] md:text-[52px] mt-4 leading-tight">
              Connected to <em className="italic text-[var(--primary)]">everywhere</em> that matters.
            </h2>
            <p className="mt-5 text-[15px] text-[var(--text-muted)] max-w-md leading-relaxed">
              Our tenants come from Weston, Davie, Pembroke Pines, Sunrise and
              beyond — the location works for the people you employ and the
              clients you visit.
            </p>
            <div className="mt-8 space-y-4 text-[14px]">
              <div className="flex items-start gap-3">
                <MapPin size={16} strokeWidth={1.5} className="mt-1 text-[var(--primary)]" />
                <span>{BRAND.address.street}, {BRAND.address.suite}<br />{BRAND.address.city}, {BRAND.address.state} {BRAND.address.zip}</span>
              </div>
              <a href={`tel:${BRAND.phoneRaw}`} className="flex items-center gap-3 hover:text-[var(--primary)]">
                <Phone size={16} strokeWidth={1.5} className="text-[var(--primary)]" />
                {BRAND.phone}
              </a>
              <a href={`tel:${BRAND.phoneSecondaryRaw}`} className="flex items-center gap-3 hover:text-[var(--primary)] -mt-2 ml-[28px] text-[var(--text-muted)] text-[13px]">
                Alt · {BRAND.phoneSecondary}
              </a>
              <a href={`mailto:${BRAND.email}`} className="flex items-center gap-3 hover:text-[var(--primary)]">
                <Mail size={16} strokeWidth={1.5} className="text-[var(--primary)]" />
                {BRAND.email}
              </a>
              <div className="flex items-start gap-3">
                <Clock size={16} strokeWidth={1.5} className="mt-1 text-[var(--primary)]" />
                <ul className="space-y-1">
                  {BRAND.hours.map((h) => (
                    <li key={h.label} className="flex justify-between gap-6">
                      <span>{h.label}</span>
                      <span className="text-[var(--text-muted)]">{h.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <Link to="/contact" className="btn-primary mt-8">
              Schedule a tour <ArrowRight size={14} />
            </Link>
          </div>
          <div className="md:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[var(--border)] hairline">
              {HIGHLIGHTS.map((h) => (
                <div key={h.title} className="bg-white p-8">
                  <h3 className="font-serif text-[24px] leading-tight">{h.title}</h3>
                  <p className="text-[14px] mt-3 text-[var(--text-muted)] leading-relaxed">{h.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    <FinalCTA title="Easiest way to see it? In person." subtitle="Pick a time that works, we'll have coffee and a curated tour ready." />
  </>
);

export default LocationPage;
